import { Button, Col, Pagination, Row, Space, Spin, Switch, Table } from 'antd';
import React, { useState } from 'react';
import {
    CheckCircleOutlined,
    CopyOutlined,
    DeleteOutlined,
    OneToOneOutlined,
    PauseCircleOutlined,
    PlusCircleOutlined,
    QuestionCircleOutlined,
    SyncOutlined,
} from '@ant-design/icons';
import { Coupon } from '../../../shared/types';
import { CouponFilter } from './CouponFilter';
import { useSortedCouponQuery } from '../hooks/useSortedCouponQuery';
import PopConfirm from '../../../shared/Components/PopConfirm';
import { useMutation } from '@apollo/client';
import { CITRIN_DELETE_COUPON, DeleteCouponRequest, DeleteCouponResponse } from '../../../GraphQL';
import CouponCreateModal from './CouponCreateModal';
import { useUserState } from '../../../Hooks/useUserState';
import { useUpdateCouponStatusMutation } from '../../../GraphQL/hooks/useUpdateCouponStatusMutation';
import { convertDateToFormat } from '../../../shared/Utils';

const CouponTable = () => {
    const [isCreateModalVisible, setCreateModalVisible] = useState(false);

    const { ...userState } = useUserState();
    const operatorId = userState?.operatorId;
    const { sortedCoupons: coupons, refetch, loading, ...pagination } = useSortedCouponQuery(operatorId);
    const updateStatusMutation = useUpdateCouponStatusMutation();
    const [deleteMutation] = useMutation<DeleteCouponResponse, DeleteCouponRequest>(CITRIN_DELETE_COUPON);
    const [isLoading, setIsLoading] = useState(false);

    const handleRemoveCoupon = (coupon: Coupon) => {
        setIsLoading(true);
        deleteMutation({ variables: { id: coupon._id } })
            .then(() => {
                refetch();
            })
            .catch((err) => console.error(err))
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleCreateCoupon = () => {
        setCreateModalVisible(true);
    };

    const handleUpdateStatusCoupon = async (coupon: Coupon, checked: boolean) => {
        setIsLoading(true);
        try {
            await updateStatusMutation({
                _id: coupon._id,
                status: checked,
            });
            refetch();
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const copyCodeToClipBoard = async (code: string) => {
        if ('clipboard' in navigator) {
            return await navigator.clipboard.writeText(code);
        } else {
            return document.execCommand('copy', true, code);
        }
    };

    const columns = [
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: 'Coupon usage',
            dataIndex: 'isMultiple',
            key: 'isMultiple',
            render: (text, record: Coupon) => {
                return record.isMultiple ? (
                    <span style={{ fontWeight: 'semi-bold', fontSize: '16px', color: '#8c8c8c' }}>
                        <SyncOutlined
                            title="Multiple Use"
                            style={{ fontSize: '16px', color: '#1890ff', marginRight: 4 }}
                        />
                        Multiple use
                    </span>
                ) : (
                    <span style={{ fontWeight: 'semi-bold', fontSize: '16px', color: '#8c8c8c' }}>
                        <OneToOneOutlined
                            title="Single use"
                            style={{ fontSize: '16px', color: '#52c41a', marginRight: 4 }}
                        />
                        Single use
                    </span>
                );
            },
        },
        {
            title: 'Target audiance',
            dataIndex: 'Target',
            key: 'isMultipleTarget',
            render: (text, record: Coupon) => {
                return record.isMultipleTarget ? (
                    <span style={{ fontWeight: 'semi-bold', fontSize: '16px', color: '#8c8c8c' }}>
                        <SyncOutlined
                            title="Multiple target"
                            style={{ fontSize: '16px', color: '#1890ff', marginRight: 4 }}
                        />
                        Multiple target
                    </span>
                ) : (
                    <span style={{ fontWeight: 'semi-bold', fontSize: '16px', color: '#8c8c8c' }}>
                        <OneToOneOutlined
                            title="Single target"
                            style={{ fontSize: '16px', color: '#52c41a', marginRight: 4 }}
                        />
                        Single target
                    </span>
                );
            },
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
        },
        {
            title: 'Reduction (%)',
            dataIndex: 'reduction',
            key: 'reduction',
        },
        {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
            render: (text, record) => {
                return (
                    <span style={{ cursor: 'pointer' }}>
                        <CopyOutlined onClick={() => copyCodeToClipBoard(record.code)} /> {text}{' '}
                    </span>
                );
            },
        },
        {
            title: 'Starting date',
            dataIndex: 'startingDate',
            key: 'startingDate',
            sorter: (a, b) => a.startingDate - b.startingDate,
            render: (text, record) => {
                return convertDateToFormat(record.startingDate, 'DD-MM-YYYY');
            },
        },
        {
            title: 'Expiration date',
            dataIndex: 'expirationDate',
            key: 'expirationDate',
            sorter: (a, b) => a.expirationDate - b.expirationDate,
            render: (text, record) => {
                return convertDateToFormat(record.expirationDate, 'DD-MM-YYYY');
            },
        },
        {
            title: 'Usage',
            dataIndex: 'isUsed',
            key: 'isUsed',
            render: (text, record: Coupon) => {
                return record.isUsed ? (
                    <span style={{ fontWeight: 'semi-bold', fontSize: '16px', color: '#8c8c8c' }}>
                        <CheckCircleOutlined title="Used" /> Used{' '}
                    </span>
                ) : (
                    <span style={{ fontWeight: 'semi-bold', fontSize: '16px', color: '#389e0d' }}>
                        <PauseCircleOutlined title="Not used" /> Not used{' '}
                    </span>
                );
            },
        },
        {
            title: 'Coupon type',
            dataIndex: 'type',
            key: 'type',
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record: Coupon) => {
                return (
                    <Space size="middle">
                        <PopConfirm
                            title={'Delete this coupon'}
                            description={'Are you sure to delete this coupon?'}
                            onConfirmTask={() => handleRemoveCoupon(record)}
                            disabled={!record.isExpiredDate}
                            popIcon={
                                <QuestionCircleOutlined
                                    style={{ color: record.isExpiredDate ? '#f71515' : '#8c8c8c' }}
                                />
                            }
                            actionIcon={
                                <DeleteOutlined
                                    style={{
                                        fontSize: '16px',
                                        color: record.isExpiredDate ? '#f71515' : '#8c8c8c',
                                    }}
                                />
                            }
                        />
                        <PopConfirm
                            title="Update status coupon"
                            description="Are you sure to update this coupon status?"
                            onConfirmTask={() => handleUpdateStatusCoupon(record, !record.status)}
                            popIcon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                            actionIcon={
                                <Switch
                                    checkedChildren="Active"
                                    unCheckedChildren="Inactive"
                                    checked={record.status}
                                    style={{
                                        backgroundColor: record.status ? '#52c41a' : '#ff4d4f',
                                    }}
                                />
                            }
                        />
                    </Space>
                );
            },
        },
    ];

    const Paginator = () => (
        <Row justify={'center'}>
            <Col>
                <Pagination
                    size="default"
                    total={pagination.totalCount}
                    pageSize={pagination.pageSize}
                    current={pagination.page}
                    showTotal={(total) => `Total Coupons/Promos ${total}`}
                    onChange={(newPage) => pagination.setPage(newPage)}
                    showQuickJumper={true}
                    showSizeChanger={false}
                    showLessItems={false}
                />
            </Col>
        </Row>
    );

    return (
        <>
            <CouponCreateModal
                updateList={refetch}
                isOpen={isCreateModalVisible}
                onRequestClose={setCreateModalVisible}
            />
            <div style={{ flex: '1 1 auto' }}>
                <Row justify={'end'}>
                    <Button
                        type="primary"
                        shape="round"
                        icon={<PlusCircleOutlined />}
                        size={'middle'}
                        onClick={handleCreateCoupon}
                    >
                        Add
                    </Button>
                </Row>

                <Row gutter={[16, 6]} style={{ paddingBottom: 10, paddingTop: 20 }}>
                    <CouponFilter />
                </Row>
                <Row gutter={16} style={{ paddingBottom: 10, paddingTop: 10 }}>
                    <Col xs={24}>
                        <Spin spinning={isLoading || loading}>
                            <Table
                                dataSource={coupons}
                                columns={columns}
                                pagination={false}
                                rowKey={() => Math.random().toString()}
                                scroll={{ x: 1500 }}
                            ></Table>
                        </Spin>
                    </Col>
                </Row>
                <Paginator />
            </div>
        </>
    );
};

export default CouponTable;
